import { copyBoardSnapshot } from './board-factory.js'
import { CARD_COLOURS, LIMITS, type CardColour, type ItemStatus } from './constants.js'
import { newId, nowIso } from './ids.js'
import { emptyCardIntent } from './intent.js'
import { boardSnapshotSchema, type BoardSnapshot } from './schemas.js'

/**
 * One-time migration of a legacy story-map export. Not a general import platform:
 * it reads the legacy dictionary shape, validates every reference before converting,
 * and reports exactly what it could not carry across. `parseImportFile` also reads
 * Blueprint's own export (Export JSON…) into a new board.
 */

export interface ImportCounts {
  columnGroups: number
  columns: number
  swimlanes: number
  rows: number
  cards: number
}

export interface ImportPreview {
  name: string
  counts: ImportCounts
  warnings: string[]
}

export type ImportResult =
  | { ok: true; preview: ImportPreview; snapshot: BoardSnapshot }
  | { ok: false; errors: string[]; warnings: string[] }

/** Card fields carried across. Everything else is dropped with a warning. */
const SUPPORTED_CARD_FIELDS = new Set([
  'id',
  'title',
  'description',
  'columnId',
  'rowId',
  'position',
  'status',
  'assignee',
  'tags',
  'color',
  'colour',
  'duration',
  'dueDate',
  'release',
])

const STATUS_ALIASES: Record<string, ItemStatus> = {
  new: 'new',
  todo: 'new',
  to_do: 'new',
  backlog: 'new',
  planned: 'planned',
  ready: 'planned',
  in_progress: 'in_progress',
  inprogress: 'in_progress',
  doing: 'in_progress',
  started: 'in_progress',
  testing: 'testing',
  blocked: 'blocked',
  on_hold: 'blocked',
  done: 'done',
  complete: 'done',
  completed: 'done',
  finished: 'done',
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

class Reporter {
  readonly errors: string[] = []
  readonly warnings: string[] = []
  private readonly seenWarnings = new Set<string>()

  error(message: string): void {
    if (this.errors.length < 50) this.errors.push(message)
  }

  /** Deduplicated: one warning per distinct problem, not one per card. */
  warn(message: string): void {
    if (this.seenWarnings.has(message)) return
    this.seenWarnings.add(message)
    if (this.warnings.length < 100) this.warnings.push(message)
  }
}

function readTitle(source: Record<string, unknown>, fallback: string, label: string, report: Reporter): string {
  const raw = source['title'] ?? source['name']
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    report.warn(`${label} had no title; used "${fallback}".`)
    return fallback
  }
  const trimmed = raw.trim()
  if (trimmed.length > LIMITS.title) {
    report.warn(`${label} title was longer than ${LIMITS.title} characters and was truncated.`)
    return trimmed.slice(0, LIMITS.title)
  }
  return trimmed
}

/** Validate an order array against its dictionary; append missing keys deterministically. */
function resolveOrder(
  order: unknown,
  dictionary: Record<string, unknown>,
  label: string,
  report: Reporter,
): string[] {
  const keys = Object.keys(dictionary)
  const listed: string[] = []
  if (order !== undefined) {
    if (!Array.isArray(order)) {
      report.error(`${label} must be an array.`)
      return []
    }
    const seen = new Set<string>()
    for (const entry of order) {
      if (typeof entry !== 'string') {
        report.error(`${label} contains a non-string entry.`)
        continue
      }
      if (seen.has(entry)) {
        report.error(`${label} lists "${entry}" more than once.`)
        continue
      }
      if (!(entry in dictionary)) {
        report.error(`${label} references unknown id "${entry}".`)
        continue
      }
      seen.add(entry)
      listed.push(entry)
    }
  }
  const listedSet = new Set(listed)
  const missing = keys.filter((key) => !listedSet.has(key)).sort()
  if (missing.length > 0) {
    report.warn(`${missing.length} entr${missing.length === 1 ? 'y was' : 'ies were'} missing from ${label} and ${missing.length === 1 ? 'was' : 'were'} appended in id order.`)
  }
  return [...listed, ...missing]
}

function readDictionary(
  source: Record<string, unknown>,
  key: string,
  required: boolean,
  report: Reporter,
): Record<string, Record<string, unknown>> {
  const value = source[key]
  if (value === undefined || value === null) {
    if (required) report.error(`Legacy board is missing the "${key}" dictionary.`)
    return {}
  }
  if (!isRecord(value)) {
    report.error(`"${key}" must be an object keyed by legacy id.`)
    return {}
  }
  const out: Record<string, Record<string, unknown>> = {}
  for (const [id, entry] of Object.entries(value)) {
    if (!isRecord(entry)) {
      report.error(`"${key}.${id}" must be an object.`)
      continue
    }
    out[id] = entry
  }
  return out
}

function convertStatus(raw: unknown, report: Reporter): ItemStatus {
  if (raw === undefined || raw === null || raw === '') return 'new'
  if (typeof raw !== 'string') {
    report.warn('A card had a non-text status; it was set to New.')
    return 'new'
  }
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_')
  const mapped = STATUS_ALIASES[key]
  if (mapped !== undefined) return mapped
  report.warn(`Status "${raw}" is not supported; those cards were set to New.`)
  return 'new'
}

function convertColour(raw: unknown, report: Reporter): CardColour {
  if (raw === undefined || raw === null || raw === '') return 'default'
  if (typeof raw !== 'string') {
    report.warn('A card had a non-text colour; it was set to Default.')
    return 'default'
  }
  const key = raw.trim().toLowerCase()
  if ((CARD_COLOURS as readonly string[]).includes(key)) return key as CardColour
  report.warn(`Colour "${raw}" is not supported; those cards were set to Default.`)
  return 'default'
}

function convertText(raw: unknown, max: number, label: string, report: Reporter): string {
  if (raw === undefined || raw === null) return ''
  if (typeof raw !== 'string') {
    report.warn(`A card had a non-text ${label}; it was cleared.`)
    return ''
  }
  const trimmed = raw.trim()
  if (trimmed.length > max) {
    report.warn(`One or more ${label} values were longer than ${max} characters and were truncated.`)
    return trimmed.slice(0, max)
  }
  return trimmed
}

function convertTags(raw: unknown, report: Reporter): string[] {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw)) {
    report.warn('A card had non-list tags; they were dropped.')
    return []
  }
  const seen = new Set<string>()
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry !== 'string') {
      report.warn('Some tags were not text and were dropped.')
      continue
    }
    let tag = entry.trim()
    if (tag.length === 0) continue
    if (tag.length > LIMITS.tag) {
      report.warn(`Some tags were longer than ${LIMITS.tag} characters and were truncated.`)
      tag = tag.slice(0, LIMITS.tag)
    }
    const key = tag.toLowerCase()
    if (seen.has(key)) continue
    if (out.length >= LIMITS.tagCount) {
      report.warn(`Some cards had more than ${LIMITS.tagCount} tags; the extras were dropped.`)
      break
    }
    seen.add(key)
    out.push(tag)
  }
  return out
}

function convertEstimate(raw: unknown, report: Reporter): number | null {
  if (raw === undefined || raw === null || raw === '') return null
  const value = typeof raw === 'string' ? Number(raw) : raw
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    report.warn('Some durations were not numeric and were cleared.')
    return null
  }
  if (value < 0) {
    report.warn('Some durations were negative and were cleared.')
    return null
  }
  if (!Number.isInteger(value)) {
    report.warn('Some durations were rounded to whole minutes.')
    return Math.round(value)
  }
  return value
}

function convertDueDate(raw: unknown, report: Reporter): string | null {
  if (raw === undefined || raw === null || raw === '') return null
  if (typeof raw !== 'string') {
    report.warn('Some due dates were not text and were cleared.')
    return null
  }
  const candidate = raw.trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    report.warn(`Due date "${raw}" is not YYYY-MM-DD; it was cleared.`)
    return null
  }
  const parsed = new Date(`${candidate}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== candidate) {
    report.warn(`Due date "${raw}" is not a real date; it was cleared.`)
    return null
  }
  return candidate
}

/** Accepts a legacy board object or `{ name, board }`. */
export function convertLegacyBoard(input: unknown, options: { boardId?: string; name?: string } = {}): ImportResult {
  const report = new Reporter()

  if (!isRecord(input)) {
    return { ok: false, errors: ['The file must contain a JSON object.'], warnings: [] }
  }

  const wrapped = isRecord(input['board'])
  const legacy = wrapped ? (input['board'] as Record<string, unknown>) : input
  const wrapperName = typeof input['name'] === 'string' ? input['name'] : undefined
  const legacyName = typeof legacy['name'] === 'string' ? legacy['name'] : undefined

  let name = (options.name ?? wrapperName ?? legacyName ?? 'Imported board').trim()
  if (name.length === 0) name = 'Imported board'
  if (name.length > LIMITS.title) {
    report.warn(`Board name was longer than ${LIMITS.title} characters and was truncated.`)
    name = name.slice(0, LIMITS.title)
  }

  const legacyGroups = readDictionary(legacy, 'columnGroups', false, report)
  const legacyColumns = readDictionary(legacy, 'columns', true, report)
  const legacySwimlanes = readDictionary(legacy, 'swimlanes', true, report)
  const legacyRows = readDictionary(legacy, 'rows', true, report)
  const legacyCards = readDictionary(legacy, 'cards', false, report)

  if (Object.keys(legacyCards).length > LIMITS.importCards) {
    report.error(`This file has ${Object.keys(legacyCards).length} cards; the limit is ${LIMITS.importCards}.`)
  }

  const columnOrder = resolveOrder(legacy['columnOrder'], legacyColumns, 'columnOrder', report)
  const swimlaneOrder = resolveOrder(legacy['swimlaneOrder'], legacySwimlanes, 'swimlaneOrder', report)

  // Rows are ordered by each swimlane's rowIds; a row's swimlaneId must agree.
  const rowOrderBySwimlane: Record<string, string[]> = {}
  const claimedRows = new Map<string, string>()
  for (const swimlaneId of swimlaneOrder) {
    const swimlane = legacySwimlanes[swimlaneId]!
    const rowIds = swimlane['rowIds']
    const listed: string[] = []
    if (rowIds !== undefined) {
      if (!Array.isArray(rowIds)) {
        report.error(`swimlanes.${swimlaneId}.rowIds must be an array.`)
      } else {
        for (const rowId of rowIds) {
          if (typeof rowId !== 'string') {
            report.error(`swimlanes.${swimlaneId}.rowIds contains a non-string entry.`)
            continue
          }
          if (!(rowId in legacyRows)) {
            report.error(`swimlanes.${swimlaneId}.rowIds references unknown row "${rowId}".`)
            continue
          }
          const owner = claimedRows.get(rowId)
          if (owner !== undefined) {
            report.error(`Row "${rowId}" is listed by both swimlane "${owner}" and "${swimlaneId}".`)
            continue
          }
          const declared = legacyRows[rowId]!['swimlaneId']
          if (typeof declared === 'string' && declared !== swimlaneId) {
            report.error(`Row "${rowId}" is listed under swimlane "${swimlaneId}" but declares swimlaneId "${declared}".`)
            continue
          }
          claimedRows.set(rowId, swimlaneId)
          listed.push(rowId)
        }
      }
    }
    rowOrderBySwimlane[swimlaneId] = listed
  }

  // Rows with a valid swimlaneId that no rowIds array mentioned are appended deterministically.
  const orphanRows: string[] = []
  for (const rowId of Object.keys(legacyRows).sort()) {
    if (claimedRows.has(rowId)) continue
    const swimlaneId = legacyRows[rowId]!['swimlaneId']
    if (typeof swimlaneId !== 'string' || !(swimlaneId in legacySwimlanes)) {
      report.error(`Row "${rowId}" does not belong to a known swimlane.`)
      continue
    }
    rowOrderBySwimlane[swimlaneId]!.push(rowId)
    claimedRows.set(rowId, swimlaneId)
    orphanRows.push(rowId)
  }
  if (orphanRows.length > 0) {
    report.warn(`${orphanRows.length} row${orphanRows.length === 1 ? ' was' : 's were'} missing from their swimlane's rowIds and ${orphanRows.length === 1 ? 'was' : 'were'} appended in id order.`)
  }

  if (report.errors.length > 0) {
    return { ok: false, errors: report.errors, warnings: report.warnings }
  }

  // --- reference rewriting -------------------------------------------------
  const boardId = options.boardId ?? newId()
  const now = nowIso()
  const groupIds = new Map<string, string>()
  const columnIds = new Map<string, string>()
  const swimlaneIds = new Map<string, string>()
  const rowIds = new Map<string, string>()
  for (const key of Object.keys(legacyGroups)) groupIds.set(key, newId())
  for (const key of columnOrder) columnIds.set(key, newId())
  for (const key of swimlaneOrder) swimlaneIds.set(key, newId())
  for (const key of claimedRows.keys()) rowIds.set(key, newId())

  const columnGroups = Object.keys(legacyGroups).map((key) => ({
    id: groupIds.get(key)!,
    boardId,
    title: readTitle(legacyGroups[key]!, 'Untitled group', `Column group "${key}"`, report),
  }))

  const columns = columnOrder.map((key, position) => {
    const source = legacyColumns[key]!
    const rawGroupId = source['groupId']
    let groupId: string | null = null
    if (typeof rawGroupId === 'string' && rawGroupId.length > 0) {
      const mapped = groupIds.get(rawGroupId)
      if (mapped === undefined) report.warn(`Column "${key}" referenced unknown group "${rawGroupId}"; the group was cleared.`)
      else groupId = mapped
    }
    return {
      id: columnIds.get(key)!,
      boardId,
      title: readTitle(source, 'Untitled column', `Column "${key}"`, report),
      groupId,
      position,
    }
  })

  const swimlanes = swimlaneOrder.map((key, position) => ({
    id: swimlaneIds.get(key)!,
    boardId,
    title: readTitle(legacySwimlanes[key]!, 'Untitled swimlane', `Swimlane "${key}"`, report),
    position,
  }))

  const rows = swimlaneOrder.flatMap((swimlaneKey) =>
    rowOrderBySwimlane[swimlaneKey]!.map((rowKey, position) => ({
      id: rowIds.get(rowKey)!,
      boardId,
      swimlaneId: swimlaneIds.get(swimlaneKey)!,
      title: readTitle(legacyRows[rowKey]!, 'Untitled row', `Row "${rowKey}"`, report),
      position,
    })),
  )

  // Cards sort by legacy position then legacy id, then compact per cell.
  const cardKeys = Object.keys(legacyCards).sort((a, b) => {
    const pa = Number(legacyCards[a]!['position'] ?? 0)
    const pb = Number(legacyCards[b]!['position'] ?? 0)
    const na = Number.isFinite(pa) ? pa : 0
    const nb = Number.isFinite(pb) ? pb : 0
    return na - nb || (a < b ? -1 : a > b ? 1 : 0)
  })

  const cellCounters = new Map<string, number>()
  const cards = []
  for (const key of cardKeys) {
    const source = legacyCards[key]!
    for (const field of Object.keys(source)) {
      if (!SUPPORTED_CARD_FIELDS.has(field)) {
        report.warn(`Card field "${field}" is not supported in this version and was omitted.`)
      }
    }
    const legacyColumnId = source['columnId']
    const legacyRowId = source['rowId']
    if (typeof legacyColumnId !== 'string' || !columnIds.has(legacyColumnId)) {
      report.error(`Card "${key}" references unknown column "${String(legacyColumnId)}".`)
      continue
    }
    if (typeof legacyRowId !== 'string' || !rowIds.has(legacyRowId)) {
      report.error(`Card "${key}" references unknown row "${String(legacyRowId)}".`)
      continue
    }
    const columnId = columnIds.get(legacyColumnId)!
    const rowId = rowIds.get(legacyRowId)!
    const cellKey = `${rowId}|${columnId}`
    const position = cellCounters.get(cellKey) ?? 0
    cellCounters.set(cellKey, position + 1)

    let title = convertText(source['title'], LIMITS.cardTitle, 'title', report)
    if (title.length === 0) {
      report.warn('Some cards had no title; they were named "Untitled card".')
      title = 'Untitled card'
    }
    let description = typeof source['description'] === 'string' ? source['description'] : ''
    if (description.length > LIMITS.description) {
      report.warn(`Some descriptions were longer than ${LIMITS.description} characters and were truncated.`)
      description = description.slice(0, LIMITS.description)
    }

    cards.push({
      id: newId(),
      boardId,
      rowId,
      columnId,
      position,
      title,
      description,
      status: convertStatus(source['status'], report),
      owner: convertText(source['assignee'], LIMITS.owner, 'assignee', report),
      tags: convertTags(source['tags'], report),
      colour: convertColour(source['color'] ?? source['colour'], report),
      estimateMinutes: convertEstimate(source['duration'], report),
      dueDate: convertDueDate(source['dueDate'], report),
      release: convertText(source['release'], LIMITS.release, 'release', report),
      // Legacy exports carry no structured intent; imported cards start empty.
      intent: emptyCardIntent(),
      createdAt: now,
      updatedAt: now,
    })
  }

  if (report.errors.length > 0) {
    return { ok: false, errors: report.errors, warnings: report.warnings }
  }

  const candidate = {
    board: { id: boardId, name, revision: 0, createdAt: now, updatedAt: now },
    columnGroups,
    columns,
    swimlanes,
    rows,
    cards,
  }

  const parsed = boardSnapshotSchema.safeParse(candidate)
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.slice(0, 20).map((issue) => `${issue.path.join('.') || 'board'}: ${issue.message}`),
      warnings: report.warnings,
    }
  }

  return {
    ok: true,
    snapshot: parsed.data,
    preview: {
      name,
      counts: {
        columnGroups: columnGroups.length,
        columns: columns.length,
        swimlanes: swimlanes.length,
        rows: rows.length,
        cards: cards.length,
      },
      warnings: report.warnings,
    },
  }
}

/**
 * Export envelope version of Blueprint's own export (Export JSON… in the board menu). Bump
 * when the snapshot shape changes so an importer can tell which reader to use.
 */
export const EXPORT_FORMAT_VERSION = 1

/** What Export JSON… writes: the board as `GET /api/boards/:id` returns it, in an envelope. */
export function boardExport(snapshot: BoardSnapshot, exportedAt: string = nowIso()) {
  return {
    formatVersion: EXPORT_FORMAT_VERSION,
    exportedAt,
    application: 'Teamcentric Blueprint',
    board: snapshot,
  }
}

/** Blueprint's own export: a `formatVersion` and a full board, which a legacy file never has. */
function isBoardExport(input: unknown): input is Record<string, unknown> {
  if (!isRecord(input) || !('formatVersion' in input)) return false
  const board = input['board']
  return isRecord(board) && isRecord(board['board'])
}

/**
 * Reads Blueprint's own export into a new board. Every id is replaced, so the board it was
 * exported from is never touched. The link to the mind map or workflow it was generated
 * from, and its findings, belong to that board and are not carried across.
 */
function convertBoardExport(input: Record<string, unknown>, options: { name?: string }): ImportResult {
  if (input['formatVersion'] !== EXPORT_FORMAT_VERSION) {
    return {
      ok: false,
      errors: [`This export has format version ${String(input['formatVersion'])}; this version of Blueprint reads version ${EXPORT_FORMAT_VERSION}.`],
      warnings: [],
    }
  }
  const parsed = boardSnapshotSchema.safeParse(input['board'])
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.slice(0, 20).map((issue) => `${issue.path.join('.') || 'board'}: ${issue.message}`),
      warnings: [],
    }
  }
  const { source, ...board } = parsed.data.board
  if (parsed.data.cards.length > LIMITS.importCards) {
    return { ok: false, errors: [`This file has ${parsed.data.cards.length} cards; the limit is ${LIMITS.importCards}.`], warnings: [] }
  }
  const warnings: string[] = []
  if (source) warnings.push('The link to the mind map or workflow this board was generated from is not carried across.')
  if (parsed.data.findings?.length) warnings.push('Findings are not carried across.')
  const name = options.name?.trim() || board.name
  const copy = copyBoardSnapshot({ ...parsed.data, board: { ...board, name } })
  return {
    ok: true,
    snapshot: copy,
    preview: {
      name,
      counts: {
        columnGroups: copy.columnGroups.length,
        columns: copy.columns.length,
        swimlanes: copy.swimlanes.length,
        rows: copy.rows.length,
        cards: copy.cards.length,
      },
      warnings,
    },
  }
}

/** Parse raw file text, enforcing the file-size guard before JSON is touched. */
export function parseImportFile(text: string, options: { boardId?: string; name?: string } = {}): ImportResult {
  const bytes = new TextEncoder().encode(text).length
  if (bytes > LIMITS.importBytes) {
    const mb = (LIMITS.importBytes / (1024 * 1024)).toFixed(0)
    return { ok: false, errors: [`The file is larger than ${mb} MB.`], warnings: [] }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    return { ok: false, errors: [`The file is not valid JSON: ${(error as Error).message}`], warnings: [] }
  }
  if (isBoardExport(parsed)) return convertBoardExport(parsed, options)
  return convertLegacyBoard(parsed, options)
}
